import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { marked } from 'marked';
import { me, channels, downgrade, autoFix, itmsServicesURL, installURL, installStatusURL, handoff } from '../lib/api.js';
import BuildComments from '../components/BuildComments.jsx';
import AutoFixHealth from '../components/AutoFixHealth.jsx';
import VisualRegressions from '../components/VisualRegressions.jsx';

export default function AppDetail() {
  const { slug } = useParams();
  const [builds, setBuilds] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [previousBuild, setPreviousBuild] = useState(null);
  const [mySubs, setMySubs] = useState([]);
  // v160.4.87: per-build install status. Map buildId → 'idle' | 'waiting' | 'installing'
  const [installStatus, setInstallStatus] = useState({});
  // v160.4.99: per-app handoff state. Holds the most recently generated
  // sign-in link for THIS app so testers landing here from a build push
  // notification can sign in without bouncing back to the apps list.
  const [handoffURL, setHandoffURL] = useState(null);

  async function generateHandoff() {
    setHandoffURL(null);
    try {
      const r = await handoff.start(slug);
      setHandoffURL(r);
    } catch (e) { setError(e.message); }
  }

  useEffect(() => {
    let cancelled = false;
    // v160.4.95h: surface fetch errors more usefully when the page is
    // opened from a push notification. Previously a generic "500: ..."
    // banner would render with no diagnostic context, making it hard to
    // know whether the issue was: (a) auth token expired, (b) backend
    // down, (c) slug doesn't match any known app, (d) network glitch.
    // Now we log full context to console (visible via Safari Web
    // Inspector when iPhone is connected) and offer a retry button.
    function loadBuilds() {
      setError('');
      me.builds(slug)
        .then(r => { if (!cancelled) setBuilds(r.builds); })
        .catch(e => {
          if (cancelled) return;
          console.error('[AppDetail] me.builds failed', {
            slug,
            error: e.message,
            stack: e.stack,
            url: window.location.href,
            navigator: { online: navigator.onLine, ua: navigator.userAgent }
          });
          setError(e.message);
        });
      channels.list()
        .then(r => { if (!cancelled) setMySubs(r.subscriptions || []); })
        .catch(err => console.warn('[AppDetail] channels.list failed', err.message));
    }
    loadBuilds();
    return () => { cancelled = true; };
  }, [slug]);

  // v160.4.96c: refresh data on three signals testers care about:
  //   1. Service worker postMessage when notification was tapped on an
  //      already-open window (the page wouldn't otherwise re-render and
  //      tester would see stale build list — root cause of the
  //      "tapping notification but I have to refresh manually" bug)
  //   2. Page visibility transitions to visible (iOS brings PWA back
  //      from background, e.g. user switches to it from home screen)
  //   3. Window focus (similar but covers different platform behaviors)
  useEffect(() => {
    let cancelled = false;

    function silentRefresh(reason) {
      // Don't reset builds to null — that would cause a UI flash. Just
      // refetch in the background and replace state when the response lands.
      console.info('[AppDetail] silent refresh', { slug, reason });
      me.builds(slug)
        .then(r => { if (!cancelled) setBuilds(r.builds); })
        .catch(err => console.warn('[AppDetail] silent refresh failed', err.message));
    }

    function onMessage(event) {
      const msg = event?.data;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'velkra-dist-refresh') {
        silentRefresh(`sw:${msg.reason || 'unknown'}`);
      }
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        silentRefresh('visibilitychange');
      }
    }

    function onFocus() {
      silentRefresh('window-focus');
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onMessage);
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onMessage);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [slug]);

  function retryLoad() {
    setBuilds(null);
    setError('');
    me.builds(slug)
      .then(r => setBuilds(r.builds))
      .catch(e => setError(e.message));
  }

  async function loadPrevious() {
    try { setPreviousBuild(await downgrade.previousBuild(slug)); }
    catch (e) { alert(e.message); }
  }

  // v160.4.87: tap-to-install with live status feedback. iOS doesn't tell
  // the page when an install succeeds, but the backend records when iOS
  // fetches the manifest (which happens only after the user accepts the
  // install dialog). We poll that and surface progress in the UI so testers
  // aren't left wondering whether anything happened.
  // v160.4.88: when the build's Info.plist declared a CFBundleURLTypes scheme,
  // attempt to launch the freshly-installed app automatically — finding the
  // new icon among 100+ home screen apps is otherwise painful.
  function startInstall(build) {
    const buildId = build.id;
    setInstallStatus(s => ({ ...s, [buildId]: 'waiting' }));
    window.location.href = itmsServicesURL(buildId);
    let polls = 0;
    const poll = async () => {
      if (polls++ >= 120) {
        setInstallStatus(s => s[buildId] === 'waiting' ? { ...s, [buildId]: 'timeout' } : s);
        return;
      }
      try {
        const r = await fetch(installStatusURL(buildId), { cache: 'no-store' });
        const data = await r.json();
        if (data.manifestFetched) {
          setInstallStatus(s => ({ ...s, [buildId]: 'installing' }));
          // Wait for the binary download to complete before attempting auto-launch.
          // The manifest fetch precedes the actual IPA download by several seconds;
          // launching too early lands on a still-installing app icon and bounces.
          if (build.url_scheme) {
            setTimeout(() => {
              try {
                window.location.href = `${build.url_scheme}://`;
              } catch { /* iOS will throw if scheme not registered — ignore */ }
            }, 6000);
          }
          return;
        }
      } catch { /* transient — keep polling */ }
      setTimeout(poll, 2000);
    };
    setTimeout(poll, 1500);
  }

  // v160.4.88: pull-to-refresh for home-screen PWA. iOS's native
  // pull-to-refresh on standalone PWAs is inconsistent across versions —
  // we implement our own so testers can always force a fresh build list.
  useEffect(() => {
    let startY = 0;
    let pulling = false;
    const onTouchStart = (e) => {
      // Only engage if scrolled to the very top — otherwise we'd hijack normal scroll.
      if (window.scrollY > 0) return;
      startY = e.touches[0].clientY;
      pulling = true;
    };
    const onTouchMove = (e) => {
      if (!pulling) return;
      const dy = e.touches[0].clientY - startY;
      if (dy > 80) {
        // Visual feedback would require a sticky pulldown indicator; for now
        // we just trigger the refresh on threshold pass.
        pulling = false;
        // Force-refresh the builds + subscriptions data.
        setBuilds(null);
        me.builds(slug).then(r => setBuilds(r.builds)).catch(e => setError(e.message));
        channels.list().then(r => setMySubs(r.subscriptions || [])).catch(() => {});
      }
    };
    const onTouchEnd = () => { pulling = false; };
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [slug]);

  async function requestFix(buildId) {
    if (!confirm('Send your bug reports for this app to be auto-fixed?\n\nClaude will analyze your reports, generate a fix, and ship a new build for you to re-test. This takes ~10 minutes.')) return;
    try {
      const r = await autoFix.request(buildId);
      if (r.alreadyQueued) {
        alert(`⏳ ${r.message}\n\nYou'll get a push notification when the new build is ready.`);
      } else {
        alert(`✅ ${r.message}\n\n${r.feedbackCount} report(s) queued for auto-fix in ${r.mode} mode.`);
      }
    } catch (e) {
      alert(`Auto-fix request: ${e.message}`);
    }
  }

  async function toggleChannel(channel) {
    const isSubbed = mySubs.some(s => s.app_slug === slug && s.channel === channel);
    try {
      if (isSubbed) await channels.unsubscribe(slug, channel);
      else await channels.subscribe(slug, channel);
      const r = await channels.list();
      setMySubs(r.subscriptions || []);
    } catch (e) { alert(e.message); }
  }

  function chanColor(c) {
    return c === 'canary' ? '#f59e0b' : c === 'experimental' ? '#ef4444' : '#10b981';
  }

  return (
    <div>
      <Link to="/" style={{ fontSize: 14, color: 'var(--text-muted)' }}>‹ All apps</Link>
      <h2 style={{ margin: '8px 0 16px', fontSize: 24 }}>{slug}</h2>

      <div className="card">
        <h3 style={{margin:0,fontSize:13}}>Channels</h3>
        <p className="muted small" style={{marginTop:4}}>Subscribe to receive notifications for builds in these channels.</p>
        <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
          {['stable','canary','experimental'].map(c => {
            const subbed = mySubs.some(s => s.app_slug === slug && s.channel === c);
            return (
              <button key={c} onClick={() => toggleChannel(c)}
                style={{padding:'6px 12px',borderRadius:8,fontSize:13,
                        background: subbed ? chanColor(c) : 'var(--bg-elev-2)',
                        color: subbed ? 'white' : 'var(--text-muted)'}}>
                {subbed ? '✓ ' : ''}{c}
              </button>
            );
          })}
        </div>
      </div>

      {/*
        v160.4.99: Per-app sign-in link. If the tester opens {slug} on the
        same iPhone where the app is installed, tapping this link launches
        the app via its custom URL scheme (e.g. aquatrack://) and seeds a
        tester JWT so feedback submission works. Without this card, the only
        way to obtain a JWT was the now-fixed Apps-list per-app handoff —
        and testers landing from a build push notification would never see
        the apps list, so they had no path to sign in.
      */}
      <div className="card">
        <h3 style={{margin:0,fontSize:13}}>📲 Sign in on this device</h3>
        <p className="muted small" style={{marginTop:4}}>
          Required for feedback (shake, long-press, element picker) to submit. Open this page in
          Mobile Safari on the iPhone where {slug} is installed, then tap the button.
        </p>
        {!handoffURL && (
          <button className="btn" style={{marginTop:8,padding:'8px 14px',fontSize:13}}
                  onClick={generateHandoff}>
            Generate sign-in link
          </button>
        )}
        {handoffURL && (
          <div style={{marginTop:8}}>
            <a className="install-cta" href={handoffURL.handoffURL}>
              Open {slug} & sign in
            </a>
            <p style={{fontSize:12,color:'var(--text-muted)',marginTop:8,textAlign:'center'}}>
              One-time use · expires in {Math.floor(handoffURL.expiresInSeconds / 60)} min
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="error-banner" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
          <div style={{flex:'1 1 auto',minWidth:0}}>
            <div style={{fontWeight:600,marginBottom:2}}>Couldn't load builds</div>
            <div style={{fontSize:13,opacity:0.85,wordBreak:'break-word'}}>{error}</div>
            {/* v160.4.95h: surface common causes inline so the user can self-diagnose */}
            <div style={{fontSize:12,opacity:0.7,marginTop:6}}>
              {error.startsWith('401') && 'Your session may have expired — sign in again.'}
              {error.startsWith('403') && "This app may not be in your tester list."}
              {error.startsWith('404') && 'App not found. The slug may be wrong.'}
              {error.startsWith('5') && 'The backend is having trouble. Try again in a moment.'}
              {(error.includes('Failed to fetch') || error.includes('NetworkError')) && 'Network problem — check your connection.'}
            </div>
          </div>
          <button
            onClick={retryLoad}
            style={{
              padding:'8px 14px',
              borderRadius:8,
              fontSize:13,
              fontWeight:600,
              background:'var(--bg-elev-2)',
              color:'var(--text)',
              border:'1px solid var(--border)',
              flexShrink:0
            }}>
            Retry
          </button>
        </div>
      )}
      {builds === null && <div className="spinner" />}
      {builds && builds.length === 0 && <div className="empty">No builds available for you yet.</div>}

      {builds && builds.length > 0 && (
        <div style={{textAlign:'right',marginBottom:8}}>
          <button onClick={loadPrevious} style={{fontSize:13,color:'var(--text-muted)',padding:'6px 10px'}}>
            🐌 Need previous build?
          </button>
        </div>
      )}
      {previousBuild && (
        <div className="card" style={{borderLeft:'3px solid var(--warning)'}}>
          <h3 style={{margin:0,fontSize:14}}>Previous build available</h3>
          <p className="muted small" style={{marginTop:4}}>v{previousBuild.build.version} (build {previousBuild.build.build_number})</p>
          <a className="install-cta" href={previousBuild.itmsServicesURL}>Install previous</a>
          <button onClick={() => setPreviousBuild(null)} style={{fontSize:12,color:'var(--text-muted)',padding:'4px',width:'100%'}}>Hide</button>
        </div>
      )}

      {builds && builds.map(b => (
        <div className="card" key={b.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                v{b.version} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 14 }}>build {b.build_number}</span>
                {b.channel && b.channel !== 'stable' && (
                  <span style={{marginLeft:8,padding:'2px 8px',borderRadius:6,fontSize:11,
                    background: chanColor(b.channel) + '33', color: chanColor(b.channel)}}>{b.channel}</span>
                )}
                {b.is_auto_fix && (
                  <span style={{marginLeft:8,padding:'2px 8px',borderRadius:6,fontSize:11,
                    background:'rgba(108,92,231,0.2)', color:'#a29bfe', fontWeight:600}}
                    title="This build was generated by Claude to fix reported bugs. Please re-test the issues you reported.">
                    🤖 auto-fix
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                {(Number(b.ipa_size_bytes) / 1048576).toFixed(1)} MB · {timeAgo(b.uploaded_at)}
              </div>
            </div>
            <span className={`badge badge-${b.state === 'released' ? 'success' : b.state === 'approved' ? 'info' : 'accent'}`}>
              {b.state}
            </span>
          </div>

          {b.release_notes && (
            <div className="markdown" style={{ marginTop: 12, fontSize: 14, color: 'var(--text-muted)' }}
                 dangerouslySetInnerHTML={{ __html: marked.parse(b.release_notes) }} />
          )}

          <button
            className="install-cta"
            onClick={() => startInstall(b)}
            disabled={installStatus[b.id] === 'installing'}
            style={{
              opacity: installStatus[b.id] === 'installing' ? 0.6 : 1,
              cursor: installStatus[b.id] === 'installing' ? 'default' : 'pointer'
            }}>
            {installStatus[b.id] === 'installing' ? '✓ Installing on this iPhone' : 'Install on this iPhone'}
          </button>

          {installStatus[b.id] === 'waiting' && (
            <div style={{
              marginTop: 8, padding: '10px 14px', borderRadius: 10,
              background: 'rgba(108,92,231,0.12)', border: '1px solid rgba(108,92,231,0.3)',
              fontSize: 13, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8
            }}>
              <span style={{
                width: 12, height: 12, border: '2px solid var(--accent)',
                borderTopColor: 'transparent', borderRadius: '50%',
                display: 'inline-block', animation: 'spin .8s linear infinite'
              }} />
              <span>Waiting for iOS — accept the install prompt</span>
            </div>
          )}

          {installStatus[b.id] === 'installing' && (
            <div style={{
              marginTop: 8, padding: '10px 14px', borderRadius: 10,
              background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)',
              fontSize: 13, color: 'var(--text)'
            }}>
              <b style={{color: '#10b981'}}>iOS is installing now.</b>
              {b.url_scheme
                ? <> The app will open automatically in a few seconds.</>
                : <> Look for the app icon on your home screen.</>
              }
              <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
                If the app won't open: <b>Settings → General → VPN &amp; Device Management</b> → trust the developer.
              </div>
            </div>
          )}

          {installStatus[b.id] === 'timeout' && (
            <div style={{
              marginTop: 8, padding: '10px 14px', borderRadius: 10,
              background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
              fontSize: 13, color: 'var(--text-muted)'
            }}>
              Still waiting — if nothing happened, you may have dismissed the install prompt. <button
                onClick={() => startInstall(b)}
                style={{color: 'var(--accent)', fontWeight: 600, padding: 0, background: 'none'}}>
                Try again
              </button>
            </div>
          )}

          <button onClick={() => requestFix(b.id)}
                  style={{display:'block', width:'100%', marginTop:8, padding:'10px',
                          background:'rgba(108,92,231,0.15)', color:'var(--accent)',
                          borderRadius:8, fontSize:13, fontWeight:600}}>
            🤖 Auto-fix my reported bugs
          </button>

          <div style={{display:'flex',justifyContent:'space-between',marginTop:8}}>
            <a href={installURL(b.id)} target="_blank" rel="noreferrer"
               style={{ fontSize: 13, color: 'var(--text-muted)' }}>Install page · QR</a>
            <button onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                    style={{fontSize:13,color:'var(--text-muted)'}}>
              💬 {expanded === b.id ? 'Hide' : 'Show'} discussion
            </button>
          </div>

          {/* v161.0 S2: visual regressions panel — silent when no regressions
              are flagged, red alert when one or more steps differ from
              the prior-build hash beyond threshold. Always rendered
              (component handles its own visibility). */}
          <VisualRegressions buildId={b.id} />

          {expanded === b.id && (
            <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid var(--border)'}}>
              <BuildComments buildId={b.id} />
            </div>
          )}
        </div>
      ))}
      {/* v160.4.99y: auto-fix verification health tile. Renders for
          admin-authed users only — non-admins get a silent no-render
          when the underlying admin endpoints return 403. */}
      <AutoFixHealth slug={slug} />
    </div>
  );
}

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
